import * as React from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

export interface SearchableSelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  onSearch?: (query: string) => void;
  onQueryChange?: (query: string) => void;
  allowFreeText?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  label,
  error,
  disabled,
  loading,
  onSearch,
  onQueryChange,
  allowFreeText = false,
}: SearchableSelectProps) {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  const lastSearchedRef = React.useRef<number>(0);
  const listboxId = React.useId();
  const selected = options.find((option) => option.value === value);
  const filtered = query
    ? options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()) || option.sublabel?.toLowerCase().includes(query.toLowerCase()))
    : options;

  React.useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  React.useEffect(() => setActiveIndex(0), [query]);
  React.useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    onQueryChange?.(nextQuery);
    setOpen(true);
    // Debounce + min 2 char threshold + throttle (max once per 300ms) for onSearch
    if (!onSearch) return;
    clearTimeout(searchDebounceRef.current);
    if (nextQuery.length < 2) {
      onSearch(nextQuery); // allow immediate clear/reset when below threshold
      return;
    }
    const now = Date.now();
    const elapsed = now - lastSearchedRef.current;
    const delay = Math.max(300 - elapsed, 0) || 300;
    searchDebounceRef.current = setTimeout(() => {
      lastSearchedRef.current = Date.now();
      onSearch(nextQuery);
    }, delay);
  }

  function handleSelect(option: SearchableSelectOption) {
    onChange(option.value);
    setQuery('');
    setOpen(false);
  }

  function handleClear() {
    onChange('');
    setQuery('');
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (filtered[activeIndex]) handleSelect(filtered[activeIndex]);
      else if (allowFreeText && query.trim()) {
        onChange(query.trim());
        setOpen(false);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {label && <label className="mb-1 block text-sm font-medium text-ink-muted">{label}</label>}
      <div className={cn('flex h-10 items-center rounded-md border bg-white px-3 text-sm', error ? 'border-danger-500' : 'border-line-strong', disabled && 'cursor-not-allowed opacity-50')}>
        <Search className="mr-2 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
        {selected && !open ? (
          <button
            type="button"
            className="flex flex-1 items-center justify-between text-left"
            onClick={() => { if (!disabled) { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); } }}
            disabled={disabled}
          >
            <span className="truncate">{selected.label}</span>
            <X
              className="ml-2 h-3.5 w-3.5 shrink-0 text-ink-subtle hover:text-ink-muted"
              aria-label={t('ui.clearSelection')}
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
            />
          </button>
        ) : (
          <>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onFocus={() => !disabled && setOpen(true)}
              onBlur={(event) => {
                const nextTarget = event.relatedTarget as Node | null;
                if (!nextTarget || !containerRef.current?.contains(nextTarget)) setOpen(false);
              }}
              onKeyDown={handleKeyDown}
              placeholder={selected ? selected.label : (placeholder ?? t('ui.select'))}
              disabled={disabled}
              className="flex-1 bg-transparent outline-none placeholder:text-ink-subtle"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-controls={listboxId}
              aria-activedescendant={open && filtered[activeIndex] ? `${listboxId}-${activeIndex}` : undefined}
            />
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
          </>
        )}
      </div>

      {open && (
        <div id={listboxId} className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-raised" role="listbox">
          {loading && <div className="px-3 py-2 text-sm text-ink-subtle">{t('common.loading')}</div>}
          {!loading && filtered.length === 0 && <div className="px-3 py-2 text-sm text-ink-subtle">{allowFreeText && query ? t('ui.pressEnterToUse') : t('ui.noResults')}</div>}
          {!loading && filtered.map((option, index) => (
            <button
              key={option.value}
              id={`${listboxId}-${index}`}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelect(option)}
              className={cn('flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-surface-hover', option.value === value && 'bg-brand-50', index === activeIndex && 'bg-surface-hover outline-none')}>
              <span className="font-medium">{option.label}</span>
              {option.sublabel && <span className="text-xs text-ink-subtle">{option.sublabel}</span>}
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
    </div>
  );
}
